/**
 * Invisible component that runs CASH-11 token verification on app launch.
 *
 * Placed inside App.tsx after AuthGate to ensure it only runs for
 * authenticated users with an active wallet. Renders nothing visible
 * but triggers the background token sweep.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module cashu/components/StartupTokenSweep
 */

import { useStartupTokenSweep } from '@/features/cashu/hooks/useStartupTokenSweep';
import type {
  StartupSweepDataSource,
  SweepWalletFactory,
} from '@/features/cashu/hooks/useStartupTokenSweep';

/** Props for StartupTokenSweep. */
export interface StartupTokenSweepProps {
  /** Data source providing mints and tokens. */
  readonly dataSource: StartupSweepDataSource;
  /** Factory for creating wallet instances per mint. */
  readonly walletFactory: SweepWalletFactory;
}

/**
 * Invisible component that triggers CASH-11 startup token sweep.
 *
 * Renders nothing. Uses useStartupTokenSweep to verify all active
 * tokens against their mints in the background.
 *
 * @param props - Data source and wallet factory
 * @returns null (renders nothing)
 */
export function StartupTokenSweep({
  dataSource,
  walletFactory,
}: StartupTokenSweepProps): React.ReactElement | null {
  useStartupTokenSweep(dataSource, walletFactory);
  return null;
}
